import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import XenditAPI from "../api/XenditAPI";
import { useWebSocket } from "../hooks/useWebSocket";

export const PAYMENT_STATES = {
    COMPLETED: {
        type: 'info',
        icon: 'ℹ',
        title: 'Transaction Processed',
        getMessage: (refNo) =>
            `Your transaction (Ref: ${refNo}) was completed. You may safely close this page and proceed to check your order status.`,
    },
    TIMEOUT: {
        type: 'pending',
        icon: '⏳',
        title: 'Verification Delay',
        getMessage: (refNo) =>
            `Payment verification for Ref: ${refNo} is taking longer than expected. Please check your payment account or order history shortly. You may safely close this window.`,
    },
    ERROR: {
        type: 'error',
        icon: '!',
        title: 'Unable to Verify',
        getMessage: (refNo) =>
            `We couldn't confirm your transaction status for (Ref: ${refNo}) automatically. Please check your payment account or order history before trying again.`,
    },
};

const PaymentStatusPage = () => {
    const navigate = useNavigate();
    const { refNo, status } = useParams();
    const [paymentState, setPaymentState] = useState(null);

    const hasExecuted = useRef(false);
    const timeoutRef = useRef(null);

    const handleWssMsg = (data) => {
        // if still verifying only process wss message
        // to prevent showing conflicted flow (conflicted flow eg: verifying > timeout > but still auto redirect)
        if (!paymentState) {
            // Clear timeout if necessary before redirecting / showing completed
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
    
            const redirectionUrl = data?.payload?.redirectionUrl;
            if (redirectionUrl) {
                window.location.href = redirectionUrl;
            } else setPaymentState(PAYMENT_STATES.COMPLETED);
        }
    };

    // websocket hook
    useWebSocket({
        topics: `payment-txn-completed-${refNo}`,
        handleOnMessage: handleWssMsg,
    });

    useEffect(() => {
        const validStatus = ['success', 'failed', 'pending'];
        if (!validStatus.includes(status)) {
            navigate('/');
            return;
        }

        // Prevent duplicate execution (including StrictMode in dev)
        if (hasExecuted.current) return;
        hasExecuted.current = true;

        const redirect = async () => {
            try {
                const res = await XenditAPI.redirect({ refNo, status });

                if (res?.redirectionUrl) { // redirect directly if have redirectUrl exists
                    window.location.href = res.redirectionUrl;
                } else if (res?.status === 'completed') { // transactions that do not have redirectUrl saved/returned
                    setPaymentState(PAYMENT_STATES.COMPLETED);
                } else if (res?.status === 'pending') {
                    // Set fallback timeout if WebSocket message doesn't arrive specific time
                    const TIMEOUT_MS = 120000; // 2 minutes

                    timeoutRef.current = setTimeout(() => {
                        setPaymentState(PAYMENT_STATES.TIMEOUT);
                    }, TIMEOUT_MS);
                } else {
                    throw new Error();
                }
            } catch (err) {
                setPaymentState(PAYMENT_STATES.ERROR);
            }
        };

        redirect();

        // Clean up timeout on unmount
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return (
        <div className="payment-status">
            <div className="page-wrapper">
                {paymentState ? (
                    /* The status-card automatically extracts all UI props from the config */
                    <div className={`status-card ${paymentState.type}`}>
                        <div className="status-icon">{paymentState.icon}</div>
                        <h3 className="status-title">{paymentState.title}</h3>
                        <p className="status-text">{paymentState.getMessage(refNo)}</p>
                    </div>
                ) : (
                    <div className="loading-card">
                        <div className="spinner"></div>
                        <h3>Verifying Your Payment...</h3>
                        <p className="subtext">Redirecting you shortly. Please wait a moment.</p>

                        <div className="warning-box">
                            ⚠️ <span>Please do not close or refresh this page while processing.</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PaymentStatusPage;
